import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import {
  ArrowLeft,
  NotebookPen,
  Plus,
  Search,
  Trash2,
  Users,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { TiptapRichTextEditor } from 'components/DesignSystems/New/TiptapRichTextEditor';
import { selectGroupGuid, useAppSelector } from 'slices/commonSelectors';
import {
  useCreateNote,
  useDeleteNote,
  useNotes,
  useUpdateNote,
} from 'hooks/notes/notesHooks';
import {
  isNoteDirty,
  noteExcerpt,
  type Note,
  type NoteDraft,
  type NoteScope,
} from 'hooks/notes/notesModel';

export function NotesView({
  scope = 'org',
  incidentId: incidentIdProp,
  openNoteId,
  onOpenNoteConsumed,
}: {
  scope?: 'org' | 'incident';
  /** Prefer this when embedding outside `/iap/:incidentId/*` (e.g. AI drawer). */
  incidentId?: string;
  /** One-shot deep-link: open this note when set (AI drawer FOC feedback). */
  openNoteId?: string | null;
  onOpenNoteConsumed?: () => void;
}) {
  const groupGuid = useAppSelector(selectGroupGuid);
  const { incidentId: incidentIdFromRoute } = useParams<{ incidentId: string }>();
  const incidentId = incidentIdProp || incidentIdFromRoute;
  const noteScope = useMemo<NoteScope | null>(() => {
    if (scope === 'incident') {
      return incidentId ? { kind: 'incident', incidentId } : null;
    }
    return groupGuid ? { kind: 'org', groupGuid } : null;
  }, [scope, incidentId, groupGuid]);

  const { data: notes = [], isLoading } = useNotes(noteScope);
  const createNote = useCreateNote(noteScope);
  const [openId, setOpenId] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!openNoteId) return;
    setOpenId(openNoteId);
    onOpenNoteConsumed?.();
  }, [openNoteId, onOpenNoteConsumed]);

  const open = notes.find((note) => note.id === openId) ?? null;

  const scopeLabel =
    scope === 'incident' ? 'this workspace' : 'your organization';

  const filteredNotes = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return notes;
    return notes.filter((note) => {
      if (note.title.toLowerCase().includes(q)) return true;
      const excerpt = noteExcerpt(note.body).toLowerCase();
      if (excerpt.includes(q)) return true;
      // Strip tags for body match without relying on excerpt truncation alone.
      const plain = note.body.replace(/<[^>]*>/g, ' ').toLowerCase();
      return plain.includes(q);
    });
  }, [notes, search]);

  if (open) {
    return (
      <NoteEditor
        key={open.id}
        note={open}
        scope={noteScope}
        scopeLabel={scopeLabel}
        onClose={() => setOpenId(null)}
      />
    );
  }

  if (openId) {
    return (
      <div className="dark tw:flex tw:h-full tw:min-h-0 tw:items-center tw:justify-center tw:p-3 tw:text-sm tw:text-muted-foreground">
        Opening note…
      </div>
    );
  }

  return (
    <div className="dark tw:flex tw:h-full tw:min-h-0 tw:flex-col tw:gap-3 tw:text-foreground">
      <div className="tw:flex tw:items-start tw:justify-between tw:gap-3">
        <div>
          <div className="tw:text-sm tw:font-medium">Notes</div>
          <p className="tw:mt-1 tw:text-xs tw:text-muted-foreground">
            Your notes are private until you share them with {scopeLabel}.
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          disabled={!noteScope || createNote.isPending}
          onClick={() =>
            createNote.mutate(
              { title: 'Untitled note' },
              { onSuccess: (note) => setOpenId(note.id) }
            )
          }
        >
          <Plus className="tw:size-4" />
          New Note
        </Button>
      </div>

      <div className="tw:relative">
        <Search className="tw:pointer-events-none tw:absolute tw:left-2.5 tw:top-1/2 tw:size-4 tw:-translate-y-1/2 tw:text-muted-foreground" />
        <Input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search notes…"
          aria-label="Search notes"
          className="tw:pl-8"
          disabled={isLoading || notes.length === 0}
        />
      </div>

      <div className="tw:min-h-0 tw:flex-1 tw:overflow-auto">
        {isLoading ? (
          <div className="tw:p-3 tw:text-xs tw:text-muted-foreground">
            Loading notes…
          </div>
        ) : notes.length === 0 ? (
          <div className="tw:flex tw:min-h-[16rem] tw:flex-col tw:items-center tw:justify-center tw:gap-2 tw:rounded-md tw:border tw:border-border tw:p-6 tw:text-center tw:text-sm tw:text-muted-foreground">
            <NotebookPen className="tw:size-6" />
            No notes yet. Create one to start writing.
          </div>
        ) : filteredNotes.length === 0 ? (
          <div className="tw:flex tw:min-h-[12rem] tw:flex-col tw:items-center tw:justify-center tw:gap-2 tw:rounded-md tw:border tw:border-border tw:p-6 tw:text-center tw:text-sm tw:text-muted-foreground">
            No notes match “{search.trim()}”.
          </div>
        ) : (
          <div className="tw:grid tw:grid-cols-1 tw:gap-3 tw:sm:grid-cols-2 tw:xl:grid-cols-3">
            {filteredNotes.map((note) => (
              <NoteCard
                key={note.id}
                note={note}
                onOpen={() => setOpenId(note.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function NoteCard({ note, onOpen }: { note: Note; onOpen: () => void }) {
  const excerpt = noteExcerpt(note.body);
  return (
    <Card size="sm" className="tw:h-full">
      <button
        type="button"
        className="tw:flex tw:h-full tw:flex-col tw:gap-2 tw:bg-transparent tw:text-left"
        onClick={onOpen}
      >
        <CardHeader className="tw:w-full">
          <CardTitle className="tw:truncate">{note.title}</CardTitle>
          <CardDescription className="tw:line-clamp-3 tw:min-h-[3em]">
            {excerpt || 'Empty note'}
          </CardDescription>
        </CardHeader>
        <CardFooter className="tw:mt-auto tw:w-full tw:flex-wrap tw:gap-2 tw:text-xs tw:text-muted-foreground">
          {note.shared ? (
            <Badge variant="secondary">
              <Users className="tw:size-3" />
              Shared
            </Badge>
          ) : (
            <Badge variant="outline">Private</Badge>
          )}
          {!note.isMine && note.createdByName ? (
            <span>by {note.createdByName}</span>
          ) : null}
          <span className="tw:ml-auto">
            {new Date(note.updatedAt).toLocaleDateString()}
          </span>
        </CardFooter>
      </button>
    </Card>
  );
}

function NoteEditor({
  note,
  scope,
  scopeLabel,
  onClose,
}: {
  note: Note;
  scope: NoteScope | null;
  scopeLabel: string;
  onClose: () => void;
}) {
  const updateNote = useUpdateNote(scope);
  const deleteNote = useDeleteNote(scope);
  const [draft, setDraft] = useState<NoteDraft>({
    title: note.title,
    body: note.body,
    shared: note.shared,
  });
  const readOnly = !note.isMine;
  const dirty = isNoteDirty(note, draft);
  const titleId = `note-title-${note.id}`;
  const shareId = `note-share-${note.id}`;
  const [discardOpen, setDiscardOpen] = useState(false);

  const save = () => {
    if (!draft.title.trim()) return;
    updateNote.mutate({
      id: note.id,
      title: draft.title.trim(),
      body: draft.body,
      shared: draft.shared,
    });
  };

  const requestClose = () => {
    if (!readOnly && dirty) setDiscardOpen(true);
    else onClose();
  };

  return (
    <div className="dark tw:flex tw:h-full tw:min-h-0 tw:flex-col tw:gap-3 tw:text-foreground">
      <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
        <Button type="button" size="sm" variant="ghost" onClick={requestClose}>
          <ArrowLeft className="tw:size-4" />
          All notes
        </Button>
        <div className="tw:ml-auto tw:flex tw:items-center tw:gap-2">
          {readOnly ? (
            <span className="tw:text-xs tw:text-muted-foreground">
              Shared by {note.createdByName ?? 'another user'} (read only)
            </span>
          ) : (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={deleteNote.isPending}
              onClick={() =>
                deleteNote.mutate(note.id, { onSuccess: onClose })
              }
            >
              <Trash2 className="tw:size-4" />
              Delete
            </Button>
          )}
        </div>
      </div>

      <div className="tw:flex tw:flex-wrap tw:items-end tw:gap-4">
        <div className="tw:flex tw:min-w-[16rem] tw:flex-1 tw:flex-col tw:gap-1">
          <Label htmlFor={titleId}>Title</Label>
          <Input
            id={titleId}
            value={draft.title}
            maxLength={200}
            readOnly={readOnly}
            onChange={(event) =>
              setDraft((prev) => ({ ...prev, title: event.target.value }))
            }
          />
        </div>
        <div className="tw:flex tw:items-center tw:gap-2 tw:pb-2">
          <Switch
            id={shareId}
            checked={draft.shared}
            disabled={readOnly}
            onCheckedChange={(checked) =>
              setDraft((prev) => ({ ...prev, shared: checked }))
            }
          />
          <Label htmlFor={shareId}>Share with {scopeLabel}</Label>
        </div>
      </div>

      <div className="tw:flex tw:min-h-0 tw:flex-1 tw:flex-col tw:gap-2">
        <div className="tw:min-h-0 tw:flex-1 tw:overflow-auto">
          <TiptapRichTextEditor
            className="tiptap-editor--notes"
            value={draft.body}
            readOnly={readOnly}
            showToolbar={!readOnly}
            enableTaskList
            minHeight="20rem"
            placeholder="Start writing…"
            onChange={(html) => {
              setDraft((prev) => ({ ...prev, body: html }));
            }}
          />
        </div>
        {!readOnly ? (
          <div className="tw:flex tw:shrink-0 tw:justify-end tw:gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={requestClose}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={!dirty || !draft.title.trim() || updateNote.isPending}
              onClick={save}
            >
              {updateNote.isPending ? 'Saving…' : dirty ? 'Save' : 'Saved'}
            </Button>
          </div>
        ) : null}
      </div>

      <AlertDialog open={discardOpen} onOpenChange={setDiscardOpen}>
        <AlertDialogContent className="dark tw:text-foreground">
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>
              Your edits to this note have not been saved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep editing</AlertDialogCancel>
            <AlertDialogAction onClick={onClose}>Discard</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
